import { ShieldCheck, GridFour, Scales, ChatCircleText, Megaphone, Trophy, FlowArrow, ChartLineUp, Funnel, Compass, Diamond, GridNine, GlobeHemisphereWest, Wrench, Gauge, ChartScatter, PuzzlePiece, UsersThree, ShieldWarning, TreeStructure, TrendUp } from "@phosphor-icons/react";

// Pillar metadata — explicit Tailwind classes (no dynamic concatenation)
export const PILLARS = {
  STRATEGY: {
    key: "STRATEGY",
    label: "Strategy",
    blurb: "Decide where to play and how to win.",
    dot: "bg-indigo-500",
    text: "text-indigo-700",
    chip: "bg-indigo-50 text-indigo-700 border border-indigo-200",
    bar: "bg-indigo-600",
    ring: "hover:border-indigo-400",
  },
  OPERATIONS: {
    key: "OPERATIONS",
    label: "Operations",
    blurb: "Plan, execute and track delivery.",
    dot: "bg-amber-500",
    text: "text-amber-700",
    chip: "bg-amber-50 text-amber-700 border border-amber-200",
    bar: "bg-amber-600",
    ring: "hover:border-amber-400",
  },
  PEOPLE: {
    key: "PEOPLE",
    label: "People",
    blurb: "Align, calibrate and coach your teams.",
    dot: "bg-emerald-500",
    text: "text-emerald-700",
    chip: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    bar: "bg-emerald-600",
    ring: "hover:border-emerald-400",
  },
};

export const PILLAR_ORDER = ["STRATEGY", "OPERATIONS", "PEOPLE"];

// Tool registry — mirrors backend TOOL_REGISTRY
export const TOOLS = {
  "focus-armor": {
    type: "focus-armor",
    name: "FOCUS·ARMOR",
    pillar: "OPERATIONS",
    kind: "checklist",
    icon: ShieldCheck,
    tagline: "Operational decision & execution tracker",
    desc: "Plan with FOCUS, then execute and track delivery with ARMOR.",
    routes: { editor: (id) => `/wizard/${id}`, view: (id) => `/track/${id}` },
  },
  eisenhower: {
    type: "eisenhower",
    name: "Eisenhower Priority Matrix",
    pillar: "STRATEGY",
    kind: "document",
    icon: GridFour,
    tagline: "Urgent / Important prioritisation",
    desc: "Sort commitments across four quadrants and route each to action.",
    routes: { editor: (id) => `/matrix/${id}`, view: (id) => `/matrix/${id}/view` },
  },
  calibration: {
    type: "calibration",
    name: "Performance & Values Calibration",
    pillar: "PEOPLE",
    kind: "document",
    icon: Scales,
    tagline: "Talent grid — performance vs values",
    desc: "Place each team member on the performance / values grid and set a clear disposition.",
    routes: { editor: (id) => `/calibration/${id}`, view: (id) => `/calibration/${id}/view` },
  },
  coaching: {
    type: "coaching",
    name: "4-Step Coaching Feedback",
    pillar: "PEOPLE",
    kind: "document",
    icon: ChatCircleText,
    tagline: "Structured developmental feedback",
    desc: "Prepare clear, fair feedback: situation, impact, desired outcome and agreement.",
    routes: { editor: (id) => `/coaching/${id}`, view: (id) => `/coaching/${id}/view` },
  },
  communication_strategy: {
    type: "communication_strategy",
    name: "Communication & Engagement Strategy",
    pillar: "PEOPLE",
    kind: "document",
    icon: Megaphone,
    tagline: "Stakeholder messaging & engagement plan",
    desc: "Map objectives, stakeholders, channels and messages — then red-team the risks.",
    routes: { editor: (id) => `/communication/${id}`, view: (id) => `/communication/${id}/view` },
  },
  playing_to_win: {
    type: "playing_to_win",
    name: "Playing to Win Cascade",
    pillar: "STRATEGY",
    kind: "document",
    icon: Trophy,
    tagline: "Five cascading strategic choices",
    desc: "Define winning aspiration, where to play, how to win, capabilities and systems.",
    routes: { editor: (id) => `/ptw/${id}`, view: (id) => `/ptw/${id}/view` },
  },
  sipoc: {
    type: "sipoc",
    name: "SIPOC Process Map",
    pillar: "OPERATIONS",
    kind: "document",
    icon: FlowArrow,
    tagline: "Suppliers · Inputs · Process · Outputs · Customers",
    desc: "Map the systemic handoffs of a process end to end on one interface sheet.",
    routes: { editor: (id) => `/sipoc/${id}`, view: (id) => `/sipoc/${id}/view` },
  },
  dmaic: {
    type: "dmaic",
    name: "DMAIC Problem Solver",
    pillar: "OPERATIONS",
    kind: "checklist",
    icon: ChartLineUp,
    tagline: "Define · Measure · Analyse · Improve · Control",
    desc: "Run a structured improvement project with a live, trackable improvement checklist.",
    routes: { editor: (id) => `/dmaic/${id}`, view: (id) => `/dmaic/${id}/track` },
  },
  toc: {
    type: "toc",
    name: "Theory of Constraints",
    pillar: "OPERATIONS",
    kind: "document",
    icon: Funnel,
    tagline: "Find & break the system bottleneck",
    desc: "Identify, exploit, subordinate, elevate and repeat to lift throughput.",
    routes: { editor: (id) => `/toc/${id}`, view: (id) => `/toc/${id}/view` },
  },
  five_forces: {
    type: "five_forces", name: "Porter's Five Forces", pillar: "STRATEGY", kind: "document", icon: Compass,
    tagline: "Industry structural pressure", desc: "Rate the five competitive forces shaping industry attractiveness.",
    routes: { editor: (id) => `/forces/${id}`, view: (id) => `/forces/${id}/view` },
  },
  vrio: {
    type: "vrio", name: "VRIO Capability Assessment", pillar: "STRATEGY", kind: "document", icon: Diamond,
    tagline: "Value · Rarity · Imitability · Organization", desc: "Run a capability through four gates to a competitive-advantage verdict.",
    routes: { editor: (id) => `/vrio/${id}`, view: (id) => `/vrio/${id}/view` },
  },
  swot: {
    type: "swot", name: "SWOT Matrix", pillar: "STRATEGY", kind: "document", icon: GridNine,
    tagline: "Strengths · Weaknesses · Opportunities · Threats", desc: "Baseline internal and external position on a 2x2 grid.",
    routes: { editor: (id) => `/swot/${id}`, view: (id) => `/swot/${id}/view` },
  },
  long_pestel: {
    type: "long_pestel", name: "LoNG PESTEL Analysis", pillar: "STRATEGY", kind: "document", icon: GlobeHemisphereWest,
    tagline: "Local · National · Global horizons", desc: "Scan six macro vectors across three geographic scopes.",
    routes: { editor: (id) => `/pestel/${id}`, view: (id) => `/pestel/${id}/view` },
  },
  ccc: {
    type: "ccc", name: "Concern · Cause · Countermeasure", pillar: "OPERATIONS", kind: "checklist", icon: Wrench,
    tagline: "High-velocity tactical problem solving", desc: "Capture the concern, drive 5-Whys, and track countermeasures live.",
    routes: { editor: (id) => `/ccc/${id}`, view: (id) => `/ccc/${id}/track` },
  },
  fmea: {
    type: "fmea", name: "FMEA Risk Grid", pillar: "OPERATIONS", kind: "document", icon: Gauge,
    tagline: "Severity × Occurrence × Detection", desc: "Score failure modes and prioritise by Risk Priority Number.",
    routes: { editor: (id) => `/fmea/${id}`, view: (id) => `/fmea/${id}/view` },
  },
  asset_lifecycle: {
    type: "asset_lifecycle", name: "Asset Lifecycle Matrix", pillar: "OPERATIONS", kind: "document", icon: ChartScatter,
    tagline: "Growth vs share capital allocation", desc: "Plot product lines or assets into four strategic archetypes.",
    routes: { editor: (id) => `/lifecycle/${id}`, view: (id) => `/lifecycle/${id}/view` },
  },
  congruence: {
    type: "congruence", name: "Nadler-Tushman Congruence", pillar: "PEOPLE", kind: "document", icon: PuzzlePiece,
    tagline: "Work · People · Structure alignment", desc: "Diagnose which organisational interfaces are misaligned.",
    routes: { editor: (id) => `/congruence/${id}`, view: (id) => `/congruence/${id}/view` },
  },
  "nine-box-matrix": {
    type: "nine-box-matrix", name: "9-Box Talent & Succession Matrix", pillar: "PEOPLE", kind: "document", icon: UsersThree,
    tagline: "Performance × potential talent grid", desc: "Plot people on a 3×3 grid and assign each a clear succession and development path.",
    routes: { editor: (id) => `/ninebox/${id}`, view: (id) => `/ninebox/${id}/view` },
  },
  "key-person-risk": {
    type: "key-person-risk", name: "Key-Person Dependency & Flight Risk Matrix", pillar: "PEOPLE", kind: "document", icon: ShieldWarning,
    tagline: "Impact × flight risk vulnerability rank", desc: "Rank critical roles by departure impact and flight risk, and flag the exposures that need a succession plan now.",
    routes: { editor: (id) => `/keyperson/${id}`, view: (id) => `/keyperson/${id}/view` },
  },
  "raci-e-grid": {
    type: "raci-e-grid", name: "RACI-E Accountability & Delegation Grid", pillar: "PEOPLE", kind: "document", icon: TreeStructure,
    tagline: "Responsible · Accountable · Consulted · Informed · Enabler", desc: "Map who owns, does, advises and enables every step — with one clear accountable owner per task.",
    routes: { editor: (id) => `/raci/${id}`, view: (id) => `/raci/${id}/view` },
  },
  ansoff: {
    type: "ansoff", name: "Ansoff Growth Matrix", pillar: "STRATEGY", kind: "document", icon: TrendUp,
    tagline: "Market × product growth options", desc: "Weigh growth paths — penetration, product, market and diversification — by relative risk.",
    routes: { editor: (id) => `/ansoff/${id}`, view: (id) => `/ansoff/${id}/view` },
  },
};

// Extract a baseline context string from any document (front-end carry-over only)
const CONTEXT_FIELD = {
  "focus-armor": (p) => p.focus?.factFind,
  eisenhower: (p) => p.context,
  calibration: (p) => p.context,
  coaching: (p) => p.situation,
  communication_strategy: (p) => p.businessGoals,
  playing_to_win: (p) => p.aspiration,
  sipoc: (p) => (p.process || []).filter(Boolean).join("\n"),
  dmaic: (p) => p.define,
  toc: (p) => p.identify,
  five_forces: (p) => p.entrants?.notes,
  vrio: (p) => p.asset,
  swot: (p) => p.strengths,
  long_pestel: (p) => p.political?.local,
  ccc: (p) => p.concern,
  fmea: (p) => (p.items?.[0]?.mode) || "",
  asset_lifecycle: (p) => (p.assets?.[0]?.name) || "",
  congruence: (p) => p.work,
  "nine-box-matrix": (p) => p.context,
  "key-person-risk": (p) => (p.roles?.[0]?.position) || "",
  "raci-e-grid": (p) => p.context,
  ansoff: (p) => p.context,
};

export function extractContext(doc) {
  const fn = CONTEXT_FIELD[doc.tool_type];
  const ctx = (fn && fn(doc.payload || {})) || "";
  return { title: doc.title || "", context: ctx };
}

export const statusLabel = (s) => (s === "Active Execution" ? "Active" : s);

export const statusStyle = {
  Draft: "bg-slate-200 text-slate-700",
  "Active Execution": "bg-blue-100 text-blue-700",
  Completed: "bg-emerald-100 text-emerald-700",
};

// Where to open a document depending on tool kind + lifecycle status
export function openPath(doc) {
  const t = TOOLS[doc.tool_type];
  if (!t) return "/dashboard";
  if (t.kind === "checklist") {
    return doc.status === "Draft" ? t.routes.editor(doc.id) : t.routes.view(doc.id);
  }
  return doc.status === "Completed" ? t.routes.view(doc.id) : t.routes.editor(doc.id);
}

// Editor entry point (used right after creating a fresh document)
export function editorPath(doc) {
  const t = TOOLS[doc.tool_type];
  if (!t) return "/dashboard";
  return t.routes.editor(doc.id);
}

export function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}
